/**
 * Multi-Slide Builder Agent Schema
 * 
 * Schema for the orchestrator agent that coordinates section-by-section builds.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE DEFINITION
// ═══════════════════════════════════════════════════════════════════════════

export const SlideDefinitionSchema = z.object({
    slideId: z.string().describe("Unique ID for the slide"),
    name: z.string().describe("Title/name of the slide"),
    slidePurpose: z.string().describe("Purpose/content description for the slide"),
});

export type SlideDefinition = z.infer<typeof SlideDefinitionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SECTION DEFINITION
// ═══════════════════════════════════════════════════════════════════════════

export const SectionDefinitionSchema = z.object({
    id: z.string().describe("Unique ID for the section"),
    name: z.string().describe("Section name/title"),
    slides: z.array(SlideDefinitionSchema).describe("Slides in this section"),
});

export type SectionDefinition = z.infer<typeof SectionDefinitionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// PRESENTATION PLAN
// ═══════════════════════════════════════════════════════════════════════════

export const PresentationPlanSchema = z.object({
    planId: z.string().describe("Plan ID"),
    title: z.string().describe("Presentation title"),
    sections: z.array(SectionDefinitionSchema).describe("Sections to build"),
    slideCount: z.number().describe("Total slide count"),
});

export type PresentationPlan = z.infer<typeof PresentationPlanSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE DIMENSIONS
// ═══════════════════════════════════════════════════════════════════════════

export const SlideDimensionsSchema = z.object({
    width: z.number().describe("Slide width in points"),
    height: z.number().describe("Slide height in points"),
});

export type SlideDimensions = z.infer<typeof SlideDimensionsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// STYLE GUIDE OVERVIEW (for prompt context)
// ═══════════════════════════════════════════════════════════════════════════

export const StyleSignatureSchema = z.object({
    hash: z.string(),
    count: z.number(),
    style: z.record(z.string(), z.any()).describe("Style properties: font_family, font_size, font_color, fill_type, fill_color, etc."),
    usage_percentage: z.number(),
});

export type StyleSignature = z.infer<typeof StyleSignatureSchema>;

export const SlideDescriptionSchema = z.object({
    slide_id: z.string().optional(),
    slide_index: z.number().optional(),
    layout_name: z.string().optional(),
    description: z.string().default(""),
    slide_type: z.string().default("unknown"),
});

export type SlideDescription = z.infer<typeof SlideDescriptionSchema>;

export const LayoutDescriptionSchema = z.object({
    layout_id: z.string(),
    description: z.string().default(""),
    master_id: z.string().optional(),
    master_name: z.string().optional(),
    has_preview_image: z.boolean().optional(),
});

export type LayoutDescription = z.infer<typeof LayoutDescriptionSchema>;

export const StyleGuideOverviewSchema = z.object({
    slideDescriptions: z.array(SlideDescriptionSchema).default([]),
    topStyleSignatures: z.array(StyleSignatureSchema).default([]),
    layoutDistribution: z.record(z.string(), z.number()).default({}),
    slideTypeDistribution: z.record(z.string(), z.number()).default({}),
    layoutDescriptions: z.array(LayoutDescriptionSchema).default([]),
});

export type StyleGuideOverview = z.infer<typeof StyleGuideOverviewSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// MULTI-SLIDE BUILDER PARAMS
// ═══════════════════════════════════════════════════════════════════════════

export const MultiSlideBuilderParamsSchema = z.object({
    planId: z.string().describe("Plan ID for this build"),
    plan: PresentationPlanSchema.describe("The presentation plan to build"),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),
    styleGuidedocumentId: z.string().describe("Deck ID of the style guide (for search)"),
    workingPresentationPath: z.string().describe("Path to working presentation"),
    workingWorkbookPath: z.string().describe("Path to working Excel workbook"),
    slideDimensions: SlideDimensionsSchema.describe("Slide dimensions in points (from working presentation)"),
    styleGuideSlideCount: z.number().int().nonnegative().describe("Number of style guide slides (for index offset calculation)"),
    styleGuideOverview: StyleGuideOverviewSchema.optional().describe("Style guide overview with slide descriptions, layouts, and top styles"),
});

export type MultiSlideBuilderParams = z.infer<typeof MultiSlideBuilderParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SECTION BUILD RESULT
// ═══════════════════════════════════════════════════════════════════════════

export const SectionBuildResultSchema = z.object({
    sectionId: z.string(),
    sectionTitle: z.string(),
    slidesBuilt: z.number(),
    success: z.boolean(),
    skipped: z.boolean().optional().describe("True if section was skipped after retry failure"),
    retried: z.boolean().optional().describe("True if section was retried"),
    errors: z.array(z.string()).optional(),
    checkpointId: z.string().optional(),
});

export type SectionBuildResult = z.infer<typeof SectionBuildResultSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// MULTI-SLIDE BUILDER RESPONSE
// ═══════════════════════════════════════════════════════════════════════════

export const MultiSlideBuilderResponseSchema = z.object({
    success: z.boolean(),
    planId: z.string(),
    sectionsBuilt: z.number(),
    sectionsSkipped: z.number().optional(),
    totalSections: z.number(),
    slidesBuilt: z.number(),
    totalSlides: z.number(),
    sectionResults: z.array(SectionBuildResultSchema),
    errors: z.array(z.string()).optional(),
    completedAt: z.string().optional(),
});

export type MultiSlideBuilderResponse = z.infer<typeof MultiSlideBuilderResponseSchema>;


